import type { UploadSlots } from "@nmt/shared";
import { post } from "../api";
import { isImage, photoInfo } from "./images";

export async function putBlob(slot: { url: string; headers: Record<string, string> }, body: Blob) {
  const res = await fetch(slot.url, { method: "PUT", headers: slot.headers, body });
  if (!res.ok) throw new Error(`Upload failed (${res.status}).`);
}

/**
 * Uploads one file to a job, contact, or the company documents (`base` is their files address),
 * with small copies for a photo. `meta` fills in what the photo itself doesn't say (a photo taken
 * in the app's camera has no EXIF: its time and place come from the phone).
 */
export async function uploadOne(base: string, file: File, opts: { category?: string; tags?: string[]; takenAt?: string | null; latitude?: number | null; longitude?: number | null } = {}) {
  const photo = isImage(file.type, file.name);
  const [slot] = await post<UploadSlots[]>(`${base}/upload-urls`, { files: [{ name: file.name, contentType: file.type || "application/octet-stream", size: file.size, variants: photo }] });
  if (!slot) throw new Error("Upload failed.");
  const info = photo ? await photoInfo(file) : null;
  await putBlob(slot.original, file);
  // Copies are best effort: without them the original shows.
  if (info?.thumb && slot.thumb) await putBlob(slot.thumb, info.thumb).catch(() => undefined);
  if (info?.medium && slot.medium) await putBlob(slot.medium, info.medium).catch(() => undefined);
  return post<{ id: string }>(base, {
    key: slot.original.key,
    thumbKey: slot.thumb?.key ?? null,
    mediumKey: slot.medium?.key ?? null,
    name: file.name,
    contentType: file.type || "application/octet-stream",
    size: file.size,
    isPhoto: photo,
    category: opts.category ?? (photo ? "Photos" : ""),
    width: info?.width ?? null,
    height: info?.height ?? null,
    takenAt: info?.takenAt ?? opts.takenAt ?? null,
    latitude: info?.latitude ?? opts.latitude ?? null,
    longitude: info?.longitude ?? opts.longitude ?? null,
    tags: opts.tags ?? [],
  });
}
